import { memo } from 'react';
import set from 'lodash/set';

import { NewlyCreatedOption, FieldTypeahead } from 'shared/ui/FieldTypeahead';
import type { OptionData } from 'shared/ui/FieldTypeahead';
import { entityTypes } from 'shared/constants';

export interface EducationTypeaheadProps {
  name: string;
  label?: string;
  placeholder?: string;
}

export interface InstitutionVM {
  institutionId: number;
  title: string;
  shortTitle?: string;
  street?: string;
  city?: string;
  province?: string;
  country?: string;
  postCode?: string;
  webSite?: string;
  logo?: string;
}

export const institutionSearchPath = (name: string, _value: InstitutionVM) =>
  [
    `${entityTypes.institutions.endpointPath}/search`,
    {
      method: 'POST',
      body: JSON.stringify({ name }),
    },
  ] as const;

export const renderCreatibleOption = (_entityType: string, valueKey: string, labelKey: string, itemName: string) => {
  const option = {
    newlyCreated: true,
    [valueKey]: itemName,
    specialLabel: translate('Save as "{{ itemName }}"', {
      itemName,
    }),
  };
  set(option, labelKey, itemName);
  return option;
};

const isNewlyCreated = (option?: InstitutionVM | NewlyCreatedOption): option is NewlyCreatedOption =>
  'newlyCreated' in option;

const formatOption = (option?: InstitutionVM | NewlyCreatedOption) => {
  if (isNewlyCreated(option)) {
    return option.specialLabel;
  }

  const address = option?.country ? (
    <>
      {option.city ? <span>{option.city},&nbsp;</span> : null}
      <span>{option.country}</span>
    </>
  ) : null;

  const location = address ? (
    <div className="text-muted small">
      <i data-testid="whed-location-icon" className="fa fa-map-marker margin-right" />
      {address}
    </div>
  ) : null;

  const acronyms = option.shortTitle?.match(/\([-0-9\p{Lu}]+\)/gu);

  const acronym = acronyms?.[0];

  return (
    <div>
      <div>
        {option.title}
        {acronym && <span className="margin-left text-muted">{acronym}</span>}
      </div>
      {location}
    </div>
  );
};

const filterOption = (option: OptionData<InstitutionVM>, label?: string) => {
  if (!label) {
    return true;
  }

  const { data } = option;

  if (!data) {
    return false;
  }

  if (data.specialLabel) {
    return true;
  }

  if (!data?.title && !data.shortTitle) {
    return false;
  }
  const trimmedLabel = label?.trim()?.toLowerCase();
  const title = data?.title?.trim()?.toLowerCase();
  const shortTitle = data?.shortTitle?.trim()?.toLowerCase();

  const result = shortTitle?.includes(trimmedLabel) || title?.includes(trimmedLabel);

  return result;
};

const InstitutionTypeahead = ({ name, label, placeholder }: EducationTypeaheadProps) => (
  <FieldTypeahead<InstitutionVM>
    creatible
    name={name}
    label={label}
    labelKey="title"
    placeholder={placeholder || translate('School name')}
    valueKey={entityTypes.institutions.entityIdKey}
    entitiesType={entityTypes.institutions.entitiesType}
    entityType={entityTypes.institutions.entityType}
    searchPath={institutionSearchPath}
    filterOption={filterOption}
    renderCreatibleOption={renderCreatibleOption}
    formatOptionLabel={formatOption}
  />
);

// eslint-disable-next-line import/no-default-export
export default memo(InstitutionTypeahead);
